import React, { useState } from "react";

import {
  List,
  DataTable,
  NumberField,
  TextField,
  TextInput,
  ReferenceInput,
  Edit,
  ReferenceField,
  SimpleForm,
  SelectInput,
  EditButton,
  NumberInput,
  Create,
  Pagination,
  Show,
  SimpleShowLayout,
  number,
  minValue,
  useRecordContext,
  useListContext,
  Button,
  TopToolbar,
  FilterButton,
  CreateButton,
  ColumnsButton,
  useNotify,
} from "react-admin";

// Lib
import { custInfo } from "../lib/customer";
import { SectionHeader } from "../lib/sectionHeader";
import { ChatBubbleTooltip } from "../lib/chatBubbleTooltip";
import { DetailsButton } from "../lib/detailsButton";
import { AdditionConfirmationField } from "../lib/additionConfirmationField";
import { AdditionRecommendationBox } from "../lib/additionRecommendationBox";
import { CustomDatetimeField, CustomDateTimeInput } from "../lib/datetime";
import { AliasOrBarcodeReference } from "../lib/aliasWithBarcodeTooltip";
import RangePickerInput from "../lib/dateRangePicker";
import { hasScope, SCOPE } from "../lib/auth";
import { DownloadCSV } from "../lib/csvDownload";
import { LotNameField } from "./lots";

// MUI
import IconDownload from "@mui/icons-material//CloudDownload";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import IconCancel from "@mui/icons-material//Cancel";
import DialogActions from "@mui/material/DialogActions";

const AdditionAppBarTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return <span> {"Vessel " + record.barcode + " Addition"}</span>;
};

const additionFilters = [
  <TextInput source="barcodeOrAlias" label="Vessel" alwaysOn resettable />,
  <TextInput source="lotName" label="Lot" alwaysOn resettable />,
  <RangePickerInput source="dateRange" label="Date" />,
];

const AdditionExportButton = () => {
  const [showDialog, setShowDialog] = useState(false);
  const { sort, filterValues } = useListContext();
  const notify = useNotify();

  const handleDownload = async () => {
    setShowDialog(false);

    try {
      await DownloadCSV(
        "BarrelWise_Additions",
        custInfo.customerID,
        "events/sulfate_addition",
        sort,
        filterValues,
      );
    } catch (err) {
      console.log(err);
      notify(`Failed to download CSV: ${err}`, { type: "error" });
    }
  };

  return (
    <>
      <Button
        onClick={() => {
          setShowDialog(true);
        }}
        label="Export"
      >
        <IconDownload />
      </Button>
      <Dialog fullWidth open={showDialog}>
        <DialogTitle>CSV Export</DialogTitle>
        <DialogActions>
          <Button
            label="ra.action.cancel"
            variant="outlined"
            onClick={() => {
              setShowDialog(false);
            }}
          >
            <IconCancel />
          </Button>
          <Button label="Download" variant="contained" onClick={handleDownload}>
            <IconDownload />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const AdditionListActions = () => {
  return (
    <TopToolbar>
      <FilterButton />
      {hasScope(SCOPE.ADMIN) ? <CreateButton /> : null}
      <AdditionExportButton />
      <ColumnsButton />
    </TopToolbar>
  );
};

export const AdditionList = () => (
  <List
    perPage={100}
    pagination={<Pagination rowsPerPageOptions={[100, 200, 500, 1000]} />}
    filters={additionFilters}
    actions={<AdditionListActions />}
    sort={{ field: "datetime", order: "DESC" }}
    storeKey={false}
    empty={false}
  >
    <DataTable rowClick={false} bulkActionButtons={false}>
      <DataTable.Col source="barrelID" label="Vessel">
        <AliasOrBarcodeReference
          source="barrelID"
          reference="vessels"
          label="Vessel"
        />
      </DataTable.Col>
      <DataTable.Col source="lotID" label="Lot">
        <ReferenceField source="lotID" reference="lots" link="show">
          <LotNameField />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="addition" label="Addition (ppm)">
        <NumberField source="addition" label="Addition (ppm)" />
      </DataTable.Col>
      <DataTable.Col source="datetime" label="Time">
        <CustomDatetimeField source="datetime" label="Time" />
      </DataTable.Col>
      <DataTable.Col source="notes" label="Notes" disableSort>
        <ChatBubbleTooltip source="notes" />
      </DataTable.Col>
      <DataTable.Col>
        <DetailsButton />
      </DataTable.Col>
      {hasScope(SCOPE.MANAGER) && (
        <DataTable.Col>
          <EditButton />
        </DataTable.Col>
      )}
    </DataTable>
  </List>
);

const validateAddition = [number(), minValue(0)];

const CreateEditForm = () => (
  <SimpleForm>
    <ReferenceInput source="barrelID" reference="vessels">
      <SelectInput optionText="barcode" label="Barcode" />
    </ReferenceInput>
    <CustomDateTimeInput source="datetime" label="Time" />
    <NumberInput
      source="addition"
      label="Addition (ppm)"
      validate={validateAddition}
    />
    <ReferenceInput source="lotID" reference="lots">
      <SelectInput optionText="name" label="Lot" />
    </ReferenceInput>
    <TextInput label="Notes" source="notes" />
  </SimpleForm>
);

export const AdditionEdit = () => (
  <Edit redirect="list">
    <CreateEditForm />
  </Edit>
);

export const AdditionCreate = () => (
  <Create redirect="list">
    <CreateEditForm />
  </Create>
);

export const AdditionShow = () => (
  <Show title={<AdditionAppBarTitle />}>
    <SimpleShowLayout>
      <SectionHeader title="Summary" />
      <AliasOrBarcodeReference
        label="Vessel"
        source="barrelID"
        reference="vessels"
        sortable={false}
      />
      <ReferenceField label="Lot" source="lotID" reference="lots" link="show">
        <LotNameField />
      </ReferenceField>
      <CustomDatetimeField source="datetime" label="Time" />
      <TextField label="Notes" source="notes" emptyText={"\u00A0"} />
      <NumberField
        source="addition"
        label="Actual Addition (ppm)"
        sortable={false}
      />
      <AdditionConfirmationField label="Confirmation" />
      <SectionHeader title="Recommendation" />
      <AdditionRecommendationBox />
    </SimpleShowLayout>
  </Show>
);
